import { createContext, ReactElement, ReactNode, useContext, useEffect, useState, useMemo, useCallback } from 'react';

import { ProfileKey } from '../../shared/types/ProfileKey';
import { logError } from '../analytics/analytics';
import useMembershipsOfPerson from '../dataHooks/useMembershipsOfPerson';
import useProfileKeys from '../dataHooks/useProfileKeys';
import useUserProfile from '../dataHooks/useUserProfile';
import Membership, { MEMBERSHIP_STATUSES } from '../types/Membership';
import Person from '../types/Person';

import { useAuth, AuthUser } from './AuthProvider';

export interface DataProviderProps {
  children?: ReactNode;
}

type UserState = {
  status:
    | 'unknown'
    | 'logged_out'
    | 'authenticating_user'
    | 'authenticating_profile'
    | 'authenticating_memberships'
    | 'ready'
    | 'error';
  error?: Error;
  authUser: AuthUser | null | undefined;
  userProfile: Person | null | undefined;
  profileKeys: ProfileKey | null | undefined;
  memberships: Membership[] | null | undefined;
};

export interface DataContextModel {
  // User state
  userState: UserState;

  // Getters
  getAllActiveOrgs: () => string[];
  getAllPendingOrgs: () => string[];
  getAllInactiveOrgs: () => string[];
  getAdminOrgs: (includePendingOrgs?: boolean) => string[];
  getAdminLeagues: () => string[];
  getMemberOrgs: () => string[];
  getAllActiveLeagues: () => string[];
  getMemberLeagues: () => [string, string][];
  getManagerTeamIds: () => string[];
  getMemberTeamIds: () => string[];

  // Membership checks
  isSelf: (person: { itemId: string }) => boolean;
  isOrgActive: (orgId: string) => boolean | undefined;
  isOrgMember: (orgId: string) => boolean;
  isOrgMemberOrAdminOfAny: () => boolean | undefined;
  isOrgMemberOfAny: () => boolean | undefined;
  isOrgMemberOrAdmin: (orgId: string) => boolean;
  isLeagueMember: (leagueId: string) => boolean;
  areInASharedOrg: (personMemberships: Membership[]) => boolean;

  // Admin checks
  isOrgAdmin: (orgId: string) => boolean;
  isOrgAdminOfAny: () => boolean | undefined;
  isLeagueAdmin: (leagueId: string, orgId?: string, membership?: Membership) => boolean | undefined;
  isEventAdmin: (eventId: string, leagueId?: string, orgId?: string) => boolean | undefined;
  isCourseAdmin: (courseId: string) => boolean | undefined;
  hasAdminRightsOverPerson: (personMemberships: Membership[]) => boolean;

  // Capability checks
  canViewPersonDetails: (person: Person, personMemberships: Membership[]) => boolean;
  canEditPerson: (person: Person) => boolean;
  canEditPersonDetails: (person: Person) => boolean;
  canCreateEvents: () => boolean;
  canCreateHandbooks: (orgId?: string) => boolean | undefined;
  canCreateLeagues: (orgId?: string) => boolean | undefined;
  canCreateNotices: (orgId?: string) => boolean | undefined;
  canCreateSeasons: (orgId?: string, leagueId?: string) => boolean | undefined;

  // Membership Management
  activeMemberships: Membership[] | undefined;
  getOrgMembership: (orgId: string, personId?: string) => Promise<Membership | null>;
  getLeagueMembership: (leagueId: string, personId?: string) => Promise<Membership | null>;
}

export const DataContext = createContext<DataContextModel>({} as DataContextModel);

export function useData(): DataContextModel {
  return useContext(DataContext);
}

export const DataProvider = ({ children }: DataProviderProps): ReactElement => {
  const { isGod, isLoggingIn, authUser } = useAuth();

  const [userState, setUserState] = useState<UserState>({
    status: 'unknown',
    authUser: undefined,
    userProfile: undefined,
    profileKeys: undefined,
    memberships: undefined,
  });

  const {
    userProfile: fetchedUserProfile,
    isUserProfileLoading,
    userProfileError,
  } = useUserProfile({ userProfileId: authUser?.profileId ?? undefined });

  const {
    profileKeys: fetchedProfileKeys,
    isProfileKeysLoading,
    profileKeysError,
  } = useProfileKeys({ profileId: authUser?.profileId ?? undefined });

  // Only query memberships once we have a profile and keys
  const shouldQueryMemberships = !!(fetchedUserProfile && fetchedProfileKeys && authUser);

  const {
    memberships: fetchedMemberships,
    isMembershipsLoading,
    membershipsError,
  } = useMembershipsOfPerson(
    shouldQueryMemberships
      ? {
          profileKeys: fetchedProfileKeys,
          personId: fetchedUserProfile.itemId,
          status: MEMBERSHIP_STATUSES.ACTIVE,
        }
      : {}
  );

  const [activeMemberships, setActiveMemberships] = useState<Membership[] | undefined>(undefined);
  const [adminLeagues, setAdminLeagues] = useState<string[]>([]);
  const [memberLeagues, setMemberLeagues] = useState<[string, string][]>([]);

  useEffect(() => {
    // Step 1: Check authentication
    if (!authUser) {
      setUserState({
        status: isLoggingIn ? 'authenticating_user' : 'logged_out',
        authUser: null,
        userProfile: null,
        profileKeys: null,
        memberships: null,
      });
      setActiveMemberships(undefined);
      return;
    }

    // Step 2: Load profile and keys
    if (isUserProfileLoading || isProfileKeysLoading) {
      setUserState({
        status: 'authenticating_profile',
        authUser: authUser,
        userProfile: null,
        profileKeys: null,
        memberships: null,
      });
      return;
    }

    // Check profile or keys error
    if (userProfileError || profileKeysError) {
      const error = userProfileError || profileKeysError;
      logError('DataProvider:profile', error);
      setUserState({
        status: 'error',
        error: error!,
        authUser: authUser,
        userProfile: null,
        profileKeys: null,
        memberships: null,
      });
      return;
    }

    // Profile and keys must be loaded to continue
    if (!fetchedUserProfile || !fetchedProfileKeys) {
      return; // Wait for profile and keys to load
    }

    // Step 3: Load memberships (only after profile is loaded)
    if (shouldQueryMemberships && isMembershipsLoading) {
      setUserState({
        status: 'authenticating_memberships',
        authUser: authUser,
        userProfile: fetchedUserProfile,
        profileKeys: fetchedProfileKeys,
        memberships: null,
      });
      return;
    }

    // Check membership error
    if (membershipsError) {
      logError('DataProvider:memberships', membershipsError);
      setUserState({
        status: 'error',
        error: membershipsError,
        authUser: authUser,
        userProfile: fetchedUserProfile,
        profileKeys: fetchedProfileKeys,
        memberships: null,
      });
      return;
    }

    // Step 4: Everything is ready
    if (fetchedUserProfile && fetchedProfileKeys && fetchedMemberships) {
      setUserState({
        status: 'ready',
        authUser: authUser,
        userProfile: fetchedUserProfile,
        profileKeys: fetchedProfileKeys,
        memberships: fetchedMemberships,
      });
      setActiveMemberships(fetchedMemberships);
    }
  }, [
    authUser,
    isLoggingIn,
    fetchedUserProfile,
    isUserProfileLoading,
    userProfileError,
    fetchedProfileKeys,
    isProfileKeysLoading,
    profileKeysError,
    fetchedMemberships,
    isMembershipsLoading,
    membershipsError,
    shouldQueryMemberships,
  ]);

  useEffect(() => {
    // Only process memberships when we're in ready state
    if (userState.status !== 'ready' || !userState.memberships) {
      return;
    }
    
    const setMembershipSubgroups = () => {
      // Get active memberships
      if (!userState.memberships) {
        return;
      }

      const filteredActiveMemberships = userState.memberships.filter((m) =>
        m.status === MEMBERSHIP_STATUSES.ACTIVE || m.status === MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE
      );
      setActiveMemberships(filteredActiveMemberships);

      // Set admin leagues - union of explicit and implicit league admin assignments
      const adminLeagueSet = new Set<string>();
      filteredActiveMemberships.forEach((m) => {
        // Add explicit league admin assignments
        if (m.adminedLeagueIdsExplicit) {
          m.adminedLeagueIdsExplicit.forEach((leagueId) => adminLeagueSet.add(leagueId));
        }
        // Add implicit league admin assignments (from being an org admin)
        if (m.adminedLeagueIdsImplicit) {
          m.adminedLeagueIdsImplicit.forEach((leagueId) => adminLeagueSet.add(leagueId));
        }
      });
      setAdminLeagues(Array.from(adminLeagueSet));
      setMemberLeagues(
        filteredActiveMemberships
          .filter((m) => m.leagues)
          .flatMap((m) => (m.leagues || []).map((leagueId) => [m.orgId, leagueId] as [string, string]))
      );
    };

    if (userState.memberships && userState.memberships.length > 0) {
      setMembershipSubgroups();
    }
  }, [userState.memberships, userState.status]);
  
  // *************** Getter functions ****************
  const getAllActiveOrgs = useCallback(
    (): string[] => {
      if (!userState.profileKeys) return [];

      const allOrgs = new Set([
        ...(userState.profileKeys.orgIds || []),
        ...(userState.profileKeys.adminOrgIds || []),
      ]);

      return Array.from(allOrgs);
    },
    [userState.profileKeys]
  );

  const getAllPendingOrgs = useCallback(
    (): string[] => {
      if (!userState.profileKeys) return [];

      const allOrgs = new Set([
        ...(userState.profileKeys.pendingOrgIds || []),
        ...(userState.profileKeys.pendingAdminOrgIds || []),
      ]);

      return Array.from(allOrgs);
    },
    [userState.profileKeys]
  );

  const getAllInactiveOrgs = useCallback(
    (): string[] => {
      if (!userState.profileKeys) return [];

      const allOrgs = new Set([
        ...(userState.profileKeys.inactiveOrgIds || []),
        ...(userState.profileKeys.inactiveAdminOrgIds || []),
      ]);

      return Array.from(allOrgs);
    },
    [userState.profileKeys]
  );

  const getAdminOrgs = useCallback(
    (includePendingOrgs: boolean = false): string[] => {
      if (!userState.profileKeys) return [];

      const adminOrgs = userState.profileKeys.adminOrgIds || [];
      if (includePendingOrgs) {
        const pendingOrgs = userState.profileKeys.pendingAdminOrgIds || [];
        return [...new Set([...adminOrgs, ...pendingOrgs])];
      }
      return adminOrgs;
    },
    [userState.profileKeys]
  );

  const getAdminLeagues = useCallback((): string[] => {
    return adminLeagues;
  }, [adminLeagues]);

  const getMemberOrgs = useCallback(
    (): string[] => {
      if (!userState.profileKeys) return [];
      return userState.profileKeys.orgIds || [];
    },
    [userState.profileKeys]
  );

  const getAllActiveLeagues = useCallback((): string[] => {
    return activeMemberships?.flatMap((m) => m.leagues || []) || [];
  }, [activeMemberships]);

  const getMemberLeagues = useCallback((): [string, string][] => {
    return memberLeagues;
  }, [memberLeagues]);

  const getManagerTeamIds = useCallback((): string[] => {
    return activeMemberships?.flatMap((m) => m.managedTeamIds ?? []) ?? [];
  }, [activeMemberships]);

  const getMemberTeamIds = useCallback((): string[] => {
    return activeMemberships?.flatMap((m) => m.teams ?? []) ?? [];
  }, [activeMemberships]);

  // *************** Membership checks ****************
  const isSelf = useCallback(
    (profile: { itemId: string }) => {
      return userState.userProfile?.itemId === profile.itemId;
    },
    [userState.userProfile]
  );

  const getAllSharedOrgs = useCallback(
    (personMemberships: Membership[], includeInactiveOrgs: boolean = false): string[] => {
      if (!userState.profileKeys || !personMemberships) {
        return [];
      }

      const userOrgs = new Set([
        ...(userState.profileKeys.orgIds || []),
        ...(userState.profileKeys.adminOrgIds || []),
      ]);

      const userAdminOrgs = new Set([
        ...(userState.profileKeys.adminOrgIds || []),
      ]);

      const sharedOrgs = new Set<string>();

      for (const membership of personMemberships) {
        const orgId = membership.orgId;
        const isActive = membership.status === MEMBERSHIP_STATUSES.ACTIVE || membership.status === MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE;
        const isPending = membership.status === MEMBERSHIP_STATUSES.PENDING;
        const isInactive = membership.status === MEMBERSHIP_STATUSES.INACTIVE;

        // Both are active members/admins
        if (isActive && userOrgs.has(orgId)) {
          sharedOrgs.add(orgId);
        }

        // User is admin, person is pending
        if (isPending && userAdminOrgs.has(orgId)) {
          sharedOrgs.add(orgId);
        }

        // User is admin, person is inactive
        if (includeInactiveOrgs && isInactive && userAdminOrgs.has(orgId)) {
          sharedOrgs.add(orgId);
        }
      }

      return Array.from(sharedOrgs);
    },
    [userState.profileKeys]
  );

  const areInASharedOrg = useCallback(
    (personMemberships: Membership[]): boolean => {
      if (isGod) {
        return true;
      }

      return getAllSharedOrgs(personMemberships, false).length > 0;
    },
    [isGod, getAllSharedOrgs]
  );

  // *************** Membership checks ****************
  const isOrgActive = useCallback(
    (orgId: string): boolean | undefined => {
      if (!userState.profileKeys) {
        return undefined;
      }

      return (
        userState.profileKeys.orgIds?.includes(orgId) || userState.profileKeys.adminOrgIds?.includes(orgId)
      );
    },
    [userState.profileKeys]
  );

  const isOrgMember = useCallback(
    (orgId: string): boolean => {
      if (!userState.profileKeys) {
        return false;
      }

      return userState.profileKeys.orgIds?.includes(orgId) || false;
    },
    [userState.profileKeys]
  );

  const isOrgMemberOrAdmin = useCallback(
    (orgId: string): boolean => {
      if (!userState.profileKeys) {
        return false;
      }

      return getAllActiveOrgs().includes(orgId);
    },
    [getAllActiveOrgs, userState.profileKeys]
  );

  const isLeagueMember = useCallback(
    (leagueId: string): boolean => {
      if (!userState.memberships) {
        return false;
      }
      return memberLeagues.some(([, lid]) => lid === leagueId);
    },
    [memberLeagues, userState.memberships]
  );

  const isOrgMemberOrAdminOfAny = useCallback((): boolean | undefined => {
    if (!userState.profileKeys) {
      return undefined;
    }

    const orgIds = [
      ...(userState.profileKeys.adminOrgIds || []),
      ...(userState.profileKeys.orgIds || []),
    ];

    if (orgIds.length > 0) {
      return true;
    } else {
      return false;
    }
  }, [userState.profileKeys]);

  const isOrgMemberOfAny = useCallback((): boolean | undefined => {
    if (!userState.profileKeys) {
      return undefined;
    }

    if (userState.profileKeys.orgIds && userState.profileKeys.orgIds.length > 0) {
      return true;
    } else {
      return false;
    }
  }, [userState.profileKeys]);

  // *************** Admin ****************
  const isOrgAdminOfAny = useCallback((): boolean | undefined => {
    if (!userState.profileKeys) {
      return undefined;
    } else if (userState.profileKeys.adminOrgIds && userState.profileKeys.adminOrgIds.length > 0) {
      return true;
    } else {
      return false;
    }
  }, [userState.profileKeys]);

  const isOrgAdmin = useCallback(
    (orgId: string): boolean => {
      if (!userState.profileKeys) {
        return false;
      }

      if (isGod || userState.profileKeys.adminOrgIds?.find((org) => org === orgId)) {
        return true;
      }

      return false;
    },
    [userState.profileKeys, isGod]
  );

  // *************** League admin checks ****************
  const isLeagueAdminOfAny = useCallback((): boolean | undefined => {
    if (!userState.profileKeys || userState.memberships === undefined) {
      return undefined;
    } else if (isOrgAdminOfAny() === true) {
      return true;
    } else if (adminLeagues.length > 0) {
      return true;
    } else {
      return false;
    }
  }, [adminLeagues, isOrgAdminOfAny, userState.profileKeys, userState.memberships]);

    const isLeagueAdmin = useCallback(
    (leagueId: string, orgId?: string, membership?: Membership): boolean | undefined => {
      if (!membership && !userState.profileKeys) {
        return false;
      }

      if (membership && membership.itemId !== userState.userProfile?.itemId) {
        return  (
          (membership.adminedLeagueIdsExplicit?.find((league) => league === leagueId)) ||
          (membership.adminedLeagueIdsImplicit?.find((league) => league === leagueId))  ? true : false
        );
      }

      if (isGod || userState.profileKeys?.adminLeagueIds?.find((league) => league === leagueId)) {
        return true;
      }

      return false;
    },
    [userState.profileKeys, userState.userProfile, isGod]
  );

  const isEventAdmin = useCallback(
    (eventId: string, leagueId?: string, orgId?: string): boolean | undefined => {
      if (!userState.profileKeys || userState.memberships === undefined) {
        return undefined;
      } else if (!userState.memberships) {
        return false;
      }

      return isGod ||
        (orgId && isOrgAdmin(orgId)) ||
        (leagueId && getAdminLeagues().includes(leagueId)) ||
        false; //  Todo: need to add logic to handle event admins.
    },
    [userState.profileKeys, userState.memberships, isGod, isOrgAdmin, getAdminLeagues]
  );

  const isCourseAdmin = useCallback(
    (courseId: string): boolean | undefined => {
      if (!userState.profileKeys || userState.memberships === undefined) {
        return undefined;
      } else if (!userState.memberships) {
        return false;
      }
      console.log("checking admin for course: ", courseId);
      return isGod; // Todo: replace when we actually add event-level admin rights
    }, [isGod, userState]
  )

  const hasAdminRightsOverPerson = useCallback(
    (personMemberships: Membership[]) => {
      if (isGod) return true;
      const adminOrgs = getAdminOrgs();
      return personMemberships.some((m) => {
        const isActiveOrInactive =
          m.status === MEMBERSHIP_STATUSES.ACTIVE ||
          m.status === MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE ||
          m.status === MEMBERSHIP_STATUSES.INACTIVE;
        return isActiveOrInactive && adminOrgs.includes(m.orgId);
      });
    },
    [isGod, getAdminOrgs]
  );

  // *************** Capability checks ****************
  const canEditPerson = useCallback(
    (person: Person): boolean => {
      return isGod || isSelf(person);
    },
    [isGod, isSelf]
  );

  const canEditPersonDetails = useCallback(
    (profile: Person): boolean => {
      return isGod || isSelf(profile);
    },
    [isGod, isSelf]
  );

  const canViewPersonDetails = useCallback(
    (person: Person, personMemberships: Membership[]): boolean => {
      if (isGod || isSelf(person)) {
        return true;
      }

      const sharedOrgs = getAllSharedOrgs(personMemberships, true);
      if (sharedOrgs.length > 0) {
        if (sharedOrgs.some((orgId) => isOrgAdmin(orgId))) {
          return true;
        }
      }
      return false;
    },
    [isGod, isSelf, getAllSharedOrgs, isOrgAdmin]
  );

  const canCreateEvents = useCallback((): boolean => {
    return Boolean(
      isGod ||
        (userState.profileKeys?.adminOrgIds &&
          (userState.profileKeys?.adminOrgIds?.length > 0 || getAdminLeagues().length > 0))
    );
  }, [isGod, getAdminLeagues, userState.profileKeys]);

  const canCreateHandbooks = useCallback(
    (orgId?: string): boolean | undefined => {
      if (!userState.profileKeys) {
        return undefined;
      } else if (orgId) {
        return isOrgAdmin(orgId);
      } else {
        return isOrgAdminOfAny();
      }
    },
    [isOrgAdmin, isOrgAdminOfAny, userState.profileKeys]
  );

  const canCreateLeagues = useCallback(
    (orgId?: string): boolean | undefined => {
      if (!userState.profileKeys) {
        return undefined;
      } else if (orgId) {
        return isOrgAdmin(orgId);
      } else {
        return isOrgAdminOfAny();
      }
    },
    [isOrgAdmin, isOrgAdminOfAny, userState.profileKeys]
  );

  const canCreateNotices = useCallback(
    (orgId?: string): boolean | undefined => {
      if (!userState.profileKeys) {
        return undefined;
      } else if (orgId) {
        return isOrgAdmin(orgId);
      } else {
        return isOrgAdminOfAny();
      }
    },
    [isOrgAdmin, isOrgAdminOfAny, userState.profileKeys]
  );

  const canCreateSeasons = useCallback(
    (orgId?: string, leagueId?: string): boolean | undefined => {
      if (!userState.profileKeys) {
        return undefined;
      } else if (leagueId && orgId) {
        return isLeagueAdmin(leagueId, orgId) || isOrgAdmin(orgId);
      } else if (orgId) {
        return isOrgAdmin(orgId);
      } else {
        return isLeagueAdminOfAny() || isOrgAdminOfAny();
      }
    },
    [isLeagueAdmin, isOrgAdmin, isLeagueAdminOfAny, isOrgAdminOfAny, userState.profileKeys]
  );

  // *************** Membership management ****************
  const getLeagueMembership = useCallback(
    async (leagueId: string, personId?: string): Promise<Membership | null> => {
      const membership = userState.memberships?.find(
        (m) => m.leagues?.includes(leagueId) && m.itemId === (personId || userState.userProfile?.itemId)
      );
      return membership || null;
    },
    [userState.memberships, userState.userProfile]
  );

  const getOrgMembership = useCallback(
    async (orgId: string, personId?: string): Promise<Membership | null> => {
      const membership = userState.memberships?.find(
        (m) => m.orgId === orgId && m.itemId === (personId || userState.userProfile?.itemId)
      );
      return membership || null;
    },
    [userState.memberships, userState.userProfile]
  );

  const values = useMemo(() => {
    return {
      // User state
      userState,
      // Getters
      getAllActiveOrgs,
      getAllPendingOrgs,
      getAllInactiveOrgs,
      getAdminOrgs,
      getAdminLeagues,
      getAllActiveLeagues,
      getMemberLeagues,
      getManagerTeamIds,
      getMemberTeamIds,
      getMemberOrgs,
      // Membership checks
      isSelf,
      isOrgActive,
      isOrgMember,
      isOrgMemberOrAdmin,
      isOrgMemberOfAny,
      isOrgMemberOrAdminOfAny,
      isLeagueMember,
      areInASharedOrg,
      // Admin checks
      isOrgAdmin,
      isOrgAdminOfAny,
      isLeagueAdmin,
      isEventAdmin,
      isCourseAdmin,
      hasAdminRightsOverPerson,
      // Capability checks
      canViewPersonDetails,
      canEditPerson,
      canEditPersonDetails,
      canCreateEvents,
      canCreateHandbooks,
      canCreateLeagues,
      canCreateNotices,
      canCreateSeasons,
      // Membership management
      activeMemberships,
      getLeagueMembership,
      getOrgMembership,
    };
  }, [
    userState,
    activeMemberships,
    getLeagueMembership,
    getOrgMembership,
    getAllActiveOrgs,
    getAllPendingOrgs,
    getAllInactiveOrgs,
    isCourseAdmin,
    isEventAdmin,
    isLeagueAdmin,
    isOrgAdmin,
    isOrgAdminOfAny,
    isLeagueMember,
    isOrgMember,
    isOrgMemberOrAdminOfAny,
    isOrgMemberOfAny,
    canCreateEvents,
    canCreateHandbooks,
    canCreateLeagues,
    canCreateNotices,
    canCreateSeasons,
    canViewPersonDetails,
    canEditPerson,
    canEditPersonDetails,
    areInASharedOrg,
    hasAdminRightsOverPerson,
    isSelf,
    isOrgActive,
    getAdminLeagues,
    getAdminOrgs,
    getAllActiveLeagues,
    getMemberLeagues,
    getManagerTeamIds,
    getMemberTeamIds,
    getMemberOrgs,
    isOrgMemberOrAdmin,
  ]);

  return <DataContext.Provider value={values}>{children}</DataContext.Provider>;
};
